import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BrimiLogo from "@/components/BrimiLogo";
import { Loader2, Mail, Lock, User, KeyRound, ArrowRight } from "lucide-react";

export default function ParentAuth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [invite, setInvite] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const r = await api.post("/parent/login", { email, password });
        localStorage.setItem("brimi_parent_token", r.data.access_token);
        toast.success("Welcome!");
        nav("/parent/dashboard");
      } else {
        const r = await api.post("/parent/register", { email, password, name, invite_code: invite });
        localStorage.setItem("brimi_parent_token", r.data.access_token);
        toast.success("Parent account created!");
        nav("/parent/dashboard");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] grid-noise flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <BrimiLogo size={44} withBadge showText textColor="white" subtitle="Parent Portal" />
        </Link>

        <div className="brimi-card p-8">
          <h1 className="font-display text-3xl text-slate-900">{mode === "login" ? "Parent Log In" : "Create Parent Account"}</h1>
          <p className="text-slate-500 mt-1">{mode === "login" ? "See your child's career progress." : "Enter the invite code your child shared with you."}</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <Field label="Your name" icon={User}>
                <input data-testid="parent-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Anita Sharma" className="flex-1 bg-transparent outline-none text-slate-900" />
              </Field>
            )}
            <Field label="Email" icon={Mail}>
              <input data-testid="parent-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="parent@example.com" className="flex-1 bg-transparent outline-none text-slate-900" />
            </Field>
            <Field label="Password" icon={Lock}>
              <input data-testid="parent-password" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="flex-1 bg-transparent outline-none text-slate-900" />
            </Field>
            {mode === "register" && (
              <Field label="Child's invite code" icon={KeyRound}>
                <input data-testid="parent-invite" value={invite} onChange={(e) => setInvite(e.target.value)} required placeholder="BRIMI-XXXXXX" className="flex-1 bg-transparent outline-none text-slate-900 uppercase" />
              </Field>
            )}
            <button data-testid="parent-submit" disabled={busy} className="w-full brimi-pill accent-coral flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{mode === "login" ? "Sign in" : "Create account"} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="text-sm text-slate-500 text-center mt-6">
            {mode === "login" ? "New parent? " : "Already have an account? "}
            <button data-testid="parent-mode-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-bold" style={{ color: "#3B82F6" }}>
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">{label}</label>
      <div className="mt-1.5 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:ring-2 focus-within:ring-blue-100">
        <Icon className="w-4 h-4 text-slate-400" />
        {children}
      </div>
    </div>
  );
}
